import { Suspense, useMemo } from 'react';

import { Provider as ReduxProvider } from 'react-redux';
import {
  createBrowserRouter,
  Navigate,
  Route,
  RouterProvider,
  Routes,
  useLocation,
} from 'react-router-dom';

import { Capability } from '@repo/utils/auth';
import AppThemeProvider from './components/AppThemeProvider';
import OutdatedClientModal from './components/OutdatedClientModal';
import {
  AuthPageLayout,
  LoadingWrapper,
  NotFoundMessage,
  ProtectedPageLayout,
} from './components/PageLayout';
import { APP_CAPABILITIES } from './components/PageLayout/apps';
import { SETTINGS_LANDING_HREF } from './components/PageLayout/settings';
import RethrowRouteError from './components/RethrowRouteError';
import SettingsModal from './components/SettingsModal';
import { SettingsOpenContext } from './components/SettingsModal/context';
import SettingsSection from './components/SettingsModal/SettingsSection';
import useSettingsBackground from './components/SettingsModal/useSettingsBackground';
import { store } from './redux/store';
import { ThemeContext, ThemeMode } from './theme';
import { ADMIN_NOTES_CAPABILITIES } from './pages/AdminNotesPage/constants';
import { QUIZ_SET_CAPABILITIES } from './pages/QuizSetPage/constants';
import { ROLL_CAPABILITIES } from './pages/StaffRollPage/constants';
import { ADMIN_VIDEOS_CAPABILITIES } from './pages/AdminVideosPage/constants';
import { CacheKey } from './utils/common/constants';
import useDarkMode from './utils/react/hooks/useDarkMode';
import useReloadOnNewBuild from './utils/react/hooks/useReloadOnNewBuild';
import lazyWithReload from './utils/react/lazyWithReload';

// Every page is lazy-loaded so each route becomes its own chunk — users only
// download the pages they actually visit (heavy editors/graphs stay out of
// the entry bundle)
const AdminClassPage = lazyWithReload(() => import('./pages/AdminClassPage'));
const AdminCoursePage = lazyWithReload(() => import('./pages/AdminCoursePage'));
const AdminLegacyExamAssignmentPage = lazyWithReload(
  () => import('./pages/AdminLegacyExamAssignmentPage')
);
const AdminLegacyExamPage = lazyWithReload(() => import('./pages/AdminLegacyExamPage'));
const AdminLegacyExamsPage = lazyWithReload(() => import('./pages/AdminLegacyExamsPage'));
const AdminFollowUpPage = lazyWithReload(() => import('./pages/AdminFollowUpPage'));
const AdminGroupPage = lazyWithReload(() => import('./pages/AdminGroupPage'));
const AdminGroupsPage = lazyWithReload(() => import('./pages/AdminGroupsPage'));
const AdminInvoicesPage = lazyWithReload(() => import('./pages/AdminInvoicesPage'));
const AdminLeadPage = lazyWithReload(() => import('./pages/AdminLeadPage'));
const AdminNotesPage = lazyWithReload(() => import('./pages/AdminNotesPage'));
const AdminPayrollPage = lazyWithReload(() => import('./pages/AdminPayrollPage'));
const AdminPeoplePage = lazyWithReload(() => import('./pages/AdminPeoplePage'));
const AdminPermissionPage = lazyWithReload(() => import('./pages/AdminPermissionPage'));
const AdminPermissionsPage = lazyWithReload(() => import('./pages/AdminPermissionsPage'));
const AdminPricePage = lazyWithReload(() => import('./pages/AdminPricePage'));
const AdminPricesPage = lazyWithReload(() => import('./pages/AdminPricesPage'));
const AdminPrintingPage = lazyWithReload(() => import('./pages/AdminPrintingPage'));
const AdminQuizSubmissionsPage = lazyWithReload(() => import('./pages/AdminQuizSubmissionsPage'));
const AdminOnboardingPreviewPage = lazyWithReload(
  () => import('./pages/AdminOnboardingPreviewPage')
);
const NileSourcesPage = lazyWithReload(() => import('./pages/NileSourcesPage'));
const NileSourcePage = lazyWithReload(() => import('./pages/NileSourcePage'));
const NileQuestionsPage = lazyWithReload(() => import('./pages/NileQuestionsPage'));
const NileQuestionPage = lazyWithReload(() => import('./pages/NileQuestionPage'));
const NileNewQuestionPage = lazyWithReload(() => import('./pages/NileNewQuestionPage'));
const NileTaxonomyPage = lazyWithReload(() => import('./pages/NileTaxonomyPage'));
const AdminReschedulePage = lazyWithReload(() => import('./pages/AdminReschedulePage'));
const AdminRollSummaryPage = lazyWithReload(() => import('./pages/AdminRollSummaryPage'));
const AdminSchoolsPage = lazyWithReload(() => import('./pages/AdminSchoolsPage'));
const AdminStaffPage = lazyWithReload(() => import('./pages/AdminUserPage/AdminStaffPage'));
const AdminStudentPage = lazyWithReload(() => import('./pages/AdminUserPage/AdminStudentPage'));
const AdminSubjectsPage = lazyWithReload(() => import('./pages/AdminSubjectsPage'));
const AdminTimesheetPage = lazyWithReload(() => import('./pages/AdminTimesheetPage'));
const AdminLegacyTopicsPage = lazyWithReload(() => import('./pages/AdminLegacyTopicsPage'));
const AdminVideoPage = lazyWithReload(() => import('./pages/AdminVideoPage'));
const AdminVideosPage = lazyWithReload(() => import('./pages/AdminVideosPage'));
const AnalyticsPage = lazyWithReload(() => import('./pages/AnalyticsPage'));
const AnalyticsQuizAttemptPage = lazyWithReload(() => import('./pages/AnalyticsPage/QuizAttempt'));
const AppsPage = lazyWithReload(() => import('./pages/AppsPage'));
const DashboardPage = lazyWithReload(() => import('./pages/DashboardPage'));
const NotesPage = lazyWithReload(() => import('./pages/NotesPage'));
const NotFoundPage = lazyWithReload(() => import('./pages/NotFoundPage'));
const QuizLivePage = lazyWithReload(() => import('./pages/QuizLivePage'));
const QuizNewSetPage = lazyWithReload(() => import('./pages/QuizNewSetPage'));
const QuizSetPage = lazyWithReload(() => import('./pages/QuizSetPage'));
const QuizSetsPage = lazyWithReload(() => import('./pages/QuizSetsPage'));
const QuizTimerPage = lazyWithReload(() => import('./pages/QuizTimerPage'));
const SettingsAccountPage = lazyWithReload(() => import('./pages/SettingsAccountPage'));
const SignInPage = lazyWithReload(() => import('./pages/SignInPage'));
const StaffClassesPage = lazyWithReload(() => import('./pages/StaffClassesPage'));
const StaffRollPage = lazyWithReload(() => import('./pages/StaffRollPage'));
const StaffStudentActivityLogPage = lazyWithReload(
  () => import('./pages/StaffStudentActivityLogPage')
);
const StudentReschedulePage = lazyWithReload(() => import('./pages/StudentReschedulePage'));
const TeacherClassPage = lazyWithReload(() => import('./pages/TeacherClassPage'));
const TeacherLegacyExamsPage = lazyWithReload(() => import('./pages/TeacherLegacyExamsPage'));
const TeacherLegacyMarkingPage = lazyWithReload(() => import('./pages/TeacherLegacyMarkingPage'));
const TrackerPage = lazyWithReload(() => import('./pages/TrackerPage'));
const TutorialsPage = lazyWithReload(() => import('./pages/TutorialsPage'));
const VideoPlayerPage = lazyWithReload(() => import('./pages/VideoPlayerPage'));
const VideosPage = lazyWithReload(() => import('./pages/VideosPage'));

/**
 * The mode pages put up on a shared screen are drawn in — the quiz on a
 * projector, the roll board on the foyer TV. A projector washes a dark page out
 * and a board is read from across the room, so they draw light whatever the
 * signed-in user's own setting.
 */
const SHARED_SCREEN_THEME_MODE = ThemeMode.Light;

/**
 * The app's routes, and the settings modal above them. Settings keep their own
 * URLs but open over the last page the reader was on, so the routes behind the
 * modal are matched against that page rather than the settings path itself.
 */
const AppRoutes = () => {
  const location = useLocation();
  const { isSettings, background, closeSettings } = useSettingsBackground();
  useReloadOnNewBuild();

  return (
    <SettingsOpenContext.Provider value={isSettings}>
      <OutdatedClientModal />
      {/* Backstop only: page chunks are caught by the boundary inside
          PageLayout, which keeps the shell up while they load */}
      <Suspense fallback={<LoadingWrapper loading />}>
        {/* The page location object in both branches, so `useRoutes` stays on
            one code path and the page behind the panel is never remounted */}
        <Routes location={isSettings ? background : location}>
          <Route
            path='signin'
            element={
              <AuthPageLayout title='Sign in'>
                <SignInPage />
              </AuthPageLayout>
            }
          />
          <Route
            path=''
            element={
              <ProtectedPageLayout title='Dashboard'>
                <DashboardPage />
              </ProtectedPageLayout>
            }
          />
          <Route
            path='notes'
            element={
              <ProtectedPageLayout title='Notes'>
                <NotesPage />
              </ProtectedPageLayout>
            }
          />
          <Route
            path='videos'
            element={
              <ProtectedPageLayout title='Videos'>
                <VideosPage />
              </ProtectedPageLayout>
            }
          />
          <Route
            path='video/:subject/:videoId'
            element={
              <ProtectedPageLayout title='Videos'>
                <VideoPlayerPage />
              </ProtectedPageLayout>
            }
          />
          <Route
            path='tutorials'
            element={
              <ProtectedPageLayout title='Tutorials'>
                <TutorialsPage />
              </ProtectedPageLayout>
            }
          />
          <Route
            path='tracker'
            element={
              <ProtectedPageLayout
                title='Tracker'
                authorisedCapabilities={[Capability.OwnAnalyticsView]}
              >
                <TrackerPage />
              </ProtectedPageLayout>
            }
          />
          <Route
            path='analytics'
            element={
              <ProtectedPageLayout
                title='Analytics'
                authorisedCapabilities={[
                  Capability.StudentAnalyticsView,
                  Capability.OwnAnalyticsView,
                ]}
              >
                <AnalyticsPage />
              </ProtectedPageLayout>
            }
          />
          <Route
            path='analytics/:studentId'
            element={
              <ProtectedPageLayout
                title='Analytics'
                authorisedCapabilities={[
                  Capability.StudentAnalyticsView,
                  Capability.OwnAnalyticsView,
                ]}
              >
                <AnalyticsPage />
              </ProtectedPageLayout>
            }
          />
          <Route
            path='analytics/:studentId/quizzes/:attemptId'
            element={
              <ProtectedPageLayout
                title='Analytics'
                authorisedCapabilities={[
                  Capability.QuizAttemptsView,
                  Capability.OwnQuizAttemptsView,
                ]}
                hidePadding
                hideDrawer
              >
                <AnalyticsQuizAttemptPage />
              </ProtectedPageLayout>
            }
          />
          <Route
            path='analytics/:subject/:examCode/:studentId/:attemptNumber'
            element={
              <ProtectedPageLayout
                title='Analytics'
                authorisedCapabilities={[
                  Capability.StudentAnalyticsView,
                  Capability.OwnAnalyticsView,
                ]}
                hidePadding
                hideDrawer
              >
                <AnalyticsPage />
              </ProtectedPageLayout>
            }
          />
          <Route
            path='analytics/:subject/:examCode/:attemptNumber'
            element={
              <ProtectedPageLayout
                title='Analytics'
                authorisedCapabilities={[
                  Capability.StudentAnalyticsView,
                  Capability.OwnAnalyticsView,
                ]}
              >
                <AnalyticsPage />
              </ProtectedPageLayout>
            }
          />
          {/* Gated on the registry, so a caller with no app behind the
                    gallery never reaches it */}
          <Route
            path='apps'
            element={
              <ProtectedPageLayout title='Apps' authorisedCapabilities={APP_CAPABILITIES}>
                <AppsPage />
              </ProtectedPageLayout>
            }
          />
          <Route
            path='admin/subjects'
            element={
              <ProtectedPageLayout
                title='Subjects'
                authorisedCapabilities={[Capability.CurriculumView]}
              >
                <AdminSubjectsPage />
              </ProtectedPageLayout>
            }
          />
          <Route
            path='admin/subjects/:subject'
            element={
              <ProtectedPageLayout
                title='Subjects'
                authorisedCapabilities={[Capability.CurriculumView]}
              >
                <AdminSubjectsPage />
              </ProtectedPageLayout>
            }
          />
          <Route
            path='admin/invoices'
            element={
              <ProtectedPageLayout
                title='Invoices'
                authorisedCapabilities={[Capability.InvoicesView]}
              >
                <AdminInvoicesPage />
              </ProtectedPageLayout>
            }
          />
          <Route
            path='admin/follow-up'
            element={
              <ProtectedPageLayout
                title='Follow up'
                authorisedCapabilities={[
                  Capability.FollowUpsView,
                  Capability.AssignedFollowUpsView,
                ]}
              >
                <AdminFollowUpPage />
              </ProtectedPageLayout>
            }
          />
          <Route
            path='admin/people'
            element={
              <ProtectedPageLayout
                title='People'
                authorisedCapabilities={[
                  Capability.StudentsViewDetails,
                  Capability.StaffViewDetails,
                  Capability.LeadsView,
                ]}
              >
                <AdminPeoplePage />
              </ProtectedPageLayout>
            }
          />
          <Route
            path='admin/student/:studentId'
            element={
              <ProtectedPageLayout
                title='Students'
                authorisedCapabilities={[Capability.StudentsViewDetails]}
              >
                <AdminStudentPage />
              </ProtectedPageLayout>
            }
          />
          <Route
            path='admin/staff/:staffId'
            element={
              <ProtectedPageLayout
                title='Staff'
                authorisedCapabilities={[Capability.StaffViewDetails]}
              >
                <AdminStaffPage />
              </ProtectedPageLayout>
            }
          />
          <Route
            path='admin/lead/:leadType/:leadId'
            element={
              <ProtectedPageLayout title='Leads' authorisedCapabilities={[Capability.LeadsView]}>
                <AdminLeadPage />
              </ProtectedPageLayout>
            }
          />
          <Route
            path='admin/course/:subject/:courseId'
            element={
              <ProtectedPageLayout
                title='Courses'
                authorisedCapabilities={[Capability.CurriculumView]}
              >
                <AdminCoursePage />
              </ProtectedPageLayout>
            }
          />
          <Route
            path='admin/course/:subject/:courseId/:streamId'
            element={
              <ProtectedPageLayout
                title='Courses'
                authorisedCapabilities={[Capability.CurriculumView]}
              >
                <AdminCoursePage />
              </ProtectedPageLayout>
            }
          />
          <Route
            path='admin/classes'
            element={
              <ProtectedPageLayout
                title='Classes'
                authorisedCapabilities={[Capability.ClassesViewAll]}
              >
                <StaffClassesPage />
              </ProtectedPageLayout>
            }
          />
          <Route
            path='admin/class/:subject/:classCode'
            element={
              <ProtectedPageLayout
                title='Classes'
                authorisedCapabilities={[Capability.ClassesViewAll]}
              >
                <AdminClassPage />
              </ProtectedPageLayout>
            }
          />
          <Route
            path='admin/video/:subject/:videoId'
            element={
              <ProtectedPageLayout title='Videos' authorisedCapabilities={[Capability.VideosEdit]}>
                <AdminVideoPage />
              </ProtectedPageLayout>
            }
          />
          <Route
            path='admin/videos/:subject'
            element={
              <ProtectedPageLayout
                title='Videos'
                authorisedCapabilities={ADMIN_VIDEOS_CAPABILITIES}
              >
                <AdminVideosPage />
              </ProtectedPageLayout>
            }
          />
          <Route
            path='admin/videos/:subject/:categoryCode'
            element={
              <ProtectedPageLayout
                title='Videos'
                authorisedCapabilities={ADMIN_VIDEOS_CAPABILITIES}
              >
                <AdminVideosPage />
              </ProtectedPageLayout>
            }
          />
          <Route
            path='admin/videos/:subject/:categoryCode/:topicCode'
            element={
              <ProtectedPageLayout
                title='Videos'
                authorisedCapabilities={ADMIN_VIDEOS_CAPABILITIES}
              >
                <AdminVideosPage />
              </ProtectedPageLayout>
            }
          />
          <Route
            path='admin/notes/:subject'
            element={
              <ProtectedPageLayout title='Notes' authorisedCapabilities={ADMIN_NOTES_CAPABILITIES}>
                <AdminNotesPage />
              </ProtectedPageLayout>
            }
          />
          <Route
            path='admin/notes/:subject/:categoryCode'
            element={
              <ProtectedPageLayout title='Notes' authorisedCapabilities={ADMIN_NOTES_CAPABILITIES}>
                <AdminNotesPage />
              </ProtectedPageLayout>
            }
          />
          <Route
            path='admin/legacy-topics/:subject/:topicGroupCode'
            element={
              <ProtectedPageLayout
                title='Topics'
                authorisedCapabilities={[Capability.LegacyTopicsEdit]}
              >
                <AdminLegacyTopicsPage />
              </ProtectedPageLayout>
            }
          />
          <Route
            path='admin/legacy-exams/:subject'
            element={
              <ProtectedPageLayout
                title='Exams'
                authorisedCapabilities={[Capability.LegacyExamsManage]}
              >
                <AdminLegacyExamsPage />
              </ProtectedPageLayout>
            }
          />
          <Route
            path='admin/legacy-exams/:subject/:examCode'
            element={
              <ProtectedPageLayout
                title='Exams'
                authorisedCapabilities={[Capability.LegacyExamsManage]}
              >
                <AdminLegacyExamPage />
              </ProtectedPageLayout>
            }
          />
          <Route
            path='admin/payroll'
            element={
              <ProtectedPageLayout
                title='Payroll'
                authorisedCapabilities={[Capability.PayrollView]}
              >
                <AdminPayrollPage />
              </ProtectedPageLayout>
            }
          />
          <Route
            path='admin/timesheet'
            element={
              <ProtectedPageLayout
                title='Timesheet'
                authorisedCapabilities={[Capability.TimesheetView]}
              >
                <AdminTimesheetPage />
              </ProtectedPageLayout>
            }
          />
          <Route
            path='admin/legacy-exam-assignment'
            element={
              <ProtectedPageLayout
                title='Exam assignment'
                authorisedCapabilities={[Capability.LegacyMarkingAssign]}
              >
                <AdminLegacyExamAssignmentPage />
              </ProtectedPageLayout>
            }
          />
          <Route
            path='admin/roll-summary'
            element={
              <ProtectedPageLayout
                title='Roll summary'
                authorisedCapabilities={[Capability.RollSummaryView]}
                hideDrawer
                hidePadding
                themeMode={SHARED_SCREEN_THEME_MODE}
              >
                <AdminRollSummaryPage />
              </ProtectedPageLayout>
            }
          />
          <Route
            path='roll'
            element={
              <ProtectedPageLayout title='Roll' authorisedCapabilities={ROLL_CAPABILITIES}>
                <StaffRollPage />
              </ProtectedPageLayout>
            }
          />
          <Route
            path='roll/:subject/:classCode/:lessonId/reschedule/:studentId'
            element={
              <ProtectedPageLayout title='Roll' authorisedCapabilities={[Capability.MakeupsManage]}>
                <AdminReschedulePage />
              </ProtectedPageLayout>
            }
          />
          <Route
            path='student/:subject/:classCode/:lessonId/reschedule'
            element={
              <ProtectedPageLayout
                title='Reschedule'
                authorisedCapabilities={[Capability.OwnMakeupsBook]}
              >
                <StudentReschedulePage />
              </ProtectedPageLayout>
            }
          />
          <Route
            path='teacher/classes'
            element={
              <ProtectedPageLayout
                title='Classes'
                authorisedCapabilities={[Capability.ClassesViewOwn]}
              >
                <StaffClassesPage />
              </ProtectedPageLayout>
            }
          />
          <Route
            path='teacher/class/:subject/:classCode'
            element={
              <ProtectedPageLayout
                title='Roll'
                authorisedCapabilities={[Capability.ClassesViewOwn]}
              >
                <TeacherClassPage />
              </ProtectedPageLayout>
            }
          />
          <Route
            path='teacher/legacy-exams'
            element={
              <ProtectedPageLayout
                title='Exams'
                authorisedCapabilities={[Capability.LegacyExamsView]}
              >
                <TeacherLegacyExamsPage />
              </ProtectedPageLayout>
            }
          />
          <Route
            path='teacher/legacy-exams/:subject/:examCode'
            element={
              <ProtectedPageLayout
                title='Exams'
                authorisedCapabilities={[Capability.LegacyExamsView]}
                hidePadding
                hideDrawer
              >
                <TeacherLegacyExamsPage />
              </ProtectedPageLayout>
            }
          />
          <Route
            path='teacher/legacy-marking'
            element={
              <ProtectedPageLayout
                title='Marking'
                authorisedCapabilities={[Capability.LegacyMarking]}
              >
                <TeacherLegacyMarkingPage />
              </ProtectedPageLayout>
            }
          />
          <Route
            path='teacher/legacy-marking/:subject/:examCode/:studentId/:attemptNumber'
            element={
              <ProtectedPageLayout
                title='Marking'
                authorisedCapabilities={[Capability.LegacyMarking, Capability.LegacyMarkingAssign]}
                hidePadding
                hideDrawer
              >
                <TeacherLegacyMarkingPage />
              </ProtectedPageLayout>
            }
          />
          <Route
            path='student-activity-log'
            element={
              <ProtectedPageLayout
                title='Activity log'
                authorisedCapabilities={[
                  Capability.ActivityLogViewAll,
                  Capability.ActivityLogViewOwn,
                ]}
              >
                <StaffStudentActivityLogPage />
              </ProtectedPageLayout>
            }
          />
          <Route
            path='admin/printing'
            element={
              <ProtectedPageLayout
                title='Printing'
                authorisedCapabilities={[Capability.PrintingUse]}
              >
                <AdminPrintingPage />
              </ProtectedPageLayout>
            }
          />
          <Route
            path='admin/onboarding-preview'
            element={
              <ProtectedPageLayout
                title='Student onboarding'
                authorisedCapabilities={[Capability.OnboardingPreview]}
              >
                <AdminOnboardingPreviewPage />
              </ProtectedPageLayout>
            }
          />
          <Route
            path='admin/quiz-assignment'
            element={
              <ProtectedPageLayout
                title='Quiz assignment'
                authorisedCapabilities={[Capability.QuizSubmissionsManage]}
              >
                <AdminQuizSubmissionsPage />
              </ProtectedPageLayout>
            }
          />
          <Route
            path='nile/sources'
            element={
              <ProtectedPageLayout
                title='Nile sources'
                authorisedCapabilities={[Capability.NileSourcesView]}
              >
                <NileSourcesPage />
              </ProtectedPageLayout>
            }
          />
          <Route
            path='nile/sources/:sourceDocumentId'
            element={
              <ProtectedPageLayout
                title='Source'
                authorisedCapabilities={[Capability.NileSourcesView]}
              >
                <NileSourcePage />
              </ProtectedPageLayout>
            }
          />
          <Route
            path='nile/questions'
            element={
              <ProtectedPageLayout
                title='Nile questions'
                authorisedCapabilities={[Capability.NileQuestionsView]}
              >
                <NileQuestionsPage />
              </ProtectedPageLayout>
            }
          />
          <Route
            path='nile/questions/new'
            element={
              <ProtectedPageLayout
                title='New Nile question'
                authorisedCapabilities={[Capability.NileQuestionsEdit]}
                hideDrawer
              >
                <NileNewQuestionPage />
              </ProtectedPageLayout>
            }
          />
          <Route
            path='nile/questions/:questionId'
            element={
              <ProtectedPageLayout
                title='Nile question'
                authorisedCapabilities={[Capability.NileQuestionsView]}
                hideDrawer
              >
                <NileQuestionPage />
              </ProtectedPageLayout>
            }
          />
          <Route
            path='nile/taxonomy'
            element={
              <ProtectedPageLayout
                title='Nile taxonomy'
                authorisedCapabilities={[Capability.NileTaxonomyView]}
              >
                <NileTaxonomyPage />
              </ProtectedPageLayout>
            }
          />
          <Route
            path='quizzes'
            element={
              <ProtectedPageLayout
                title='Quizzes'
                authorisedCapabilities={[Capability.QuestionSetsView]}
              >
                <QuizSetsPage />
              </ProtectedPageLayout>
            }
          />
          <Route
            path='quizzes/new'
            element={
              <ProtectedPageLayout
                title='Quizzes'
                authorisedCapabilities={[Capability.QuestionSetsEdit]}
              >
                <QuizNewSetPage />
              </ProtectedPageLayout>
            }
          />
          <Route
            path='quizzes/timer'
            element={
              <ProtectedPageLayout
                title='Quizzes'
                authorisedCapabilities={[Capability.QuizzesRun]}
                hideDrawer
                hidePadding
                themeMode={SHARED_SCREEN_THEME_MODE}
              >
                <QuizTimerPage />
              </ProtectedPageLayout>
            }
          />
          <Route
            path='quiz/:setId'
            element={
              <ProtectedPageLayout title='Quizzes' authorisedCapabilities={QUIZ_SET_CAPABILITIES}>
                <QuizSetPage />
              </ProtectedPageLayout>
            }
          />
          <Route
            path='quiz/:setId/live'
            element={
              <ProtectedPageLayout
                title='Quizzes'
                authorisedCapabilities={[Capability.QuizzesRun]}
                hideDrawer
                hidePadding
                themeMode={SHARED_SCREEN_THEME_MODE}
              >
                <QuizLivePage />
              </ProtectedPageLayout>
            }
          />
          <Route
            path='*'
            element={
              <ProtectedPageLayout title='Error 404'>
                <NotFoundPage />
              </ProtectedPageLayout>
            }
          />
        </Routes>
      </Suspense>
      {isSettings && (
        <SettingsModal onClose={closeSettings}>
          <Routes>
            <Route path='settings' element={<Navigate replace to={SETTINGS_LANDING_HREF} />} />
            <Route
              path='settings/account'
              element={
                <SettingsSection title='Account'>
                  <SettingsAccountPage />
                </SettingsSection>
              }
            />
            <Route
              path='settings/access/permissions'
              element={
                <SettingsSection
                  title='Permissions'
                  authorisedCapabilities={[Capability.ContentAccessView]}
                >
                  <AdminPermissionsPage />
                </SettingsSection>
              }
            />
            <Route
              path='settings/access/permissions/:permissionId'
              element={
                <SettingsSection
                  title='Permissions'
                  authorisedCapabilities={[Capability.ContentAccessView]}
                >
                  <AdminPermissionPage />
                </SettingsSection>
              }
            />
            <Route
              path='settings/access/groups'
              element={
                <SettingsSection
                  title='Groups'
                  authorisedCapabilities={[Capability.ContentAccessView]}
                >
                  <AdminGroupsPage />
                </SettingsSection>
              }
            />
            <Route
              path='settings/access/groups/:permissionGroupId'
              element={
                <SettingsSection
                  title='Groups'
                  authorisedCapabilities={[Capability.ContentAccessView]}
                >
                  <AdminGroupPage />
                </SettingsSection>
              }
            />
            <Route
              path='settings/schools'
              element={
                <SettingsSection title='Schools' authorisedCapabilities={[Capability.SchoolsView]}>
                  <AdminSchoolsPage />
                </SettingsSection>
              }
            />
            <Route
              path='settings/prices'
              element={
                <SettingsSection title='Prices' authorisedCapabilities={[Capability.PricesView]}>
                  <AdminPricesPage />
                </SettingsSection>
              }
            />
            <Route
              path='settings/prices/:priceId'
              element={
                <SettingsSection title='Prices' authorisedCapabilities={[Capability.PricesView]}>
                  <AdminPricePage />
                </SettingsSection>
              }
            />
            {/* A settings path naming no section, which the page routes behind
                the modal never see */}
            <Route
              path='*'
              element={
                <SettingsSection title='Error 404'>
                  <NotFoundMessage />
                </SettingsSection>
              }
            />
          </Routes>
        </SettingsModal>
      )}
    </SettingsOpenContext.Provider>
  );
};

// A data router, which `useBlocker` needs; the app's own routes live in `AppRoutes`
const router = createBrowserRouter([
  { path: '*', element: <AppRoutes />, errorElement: <RethrowRouteError /> },
]);

const ThemedApp = () => {
  const browserThemeMode = localStorage.getItem(CacheKey.ThemeMode);
  // Prior to reading the user's settings, default to the last theme mode
  // experienced by the browser
  const { darkMode, initDarkMode, initLightMode } = useDarkMode(
    browserThemeMode === ThemeMode.Dark
  );

  const contextValue = useMemo(
    () => ({ initDarkMode, initLightMode }),
    [initDarkMode, initLightMode]
  );

  return (
    <ThemeContext.Provider value={contextValue}>
      <AppThemeProvider mode={darkMode ? ThemeMode.Dark : ThemeMode.Light}>
        <RouterProvider router={router} />
      </AppThemeProvider>
    </ThemeContext.Provider>
  );
};

const App = () => (
  <ReduxProvider store={store}>
    <ThemedApp />
  </ReduxProvider>
);

export default App;
